import React, { useContext, useEffect, useRef, useState } from "react";
import { motion, useAnimation } from "framer-motion";
import InfinityCard from "../InfinityCard";
import CharacterCard from "../CharacterCard";
import SpriteAnimator from "./SpriteAnimator";
import { MainContext } from "../../App";
import AudioManager from "../../@shared/utils/AudioManager";
import starShine from "../../@shared/assets/icons/star-shine.webp";
import backImage from "../../@shared/assets/back-of-card.png";

const CARD_ASPECT_RATIO = 2.5 / 3.5;
const REVEAL_FRONT_ROTATE_Y = 180.1;
const FALL_FORWARD_FRONT_ROTATE_X = 180.1;

const cardFaceStyle = {
  position: "absolute",
  inset: 0,
  borderRadius: "12px",
  overflow: "hidden",
  backfaceVisibility: "hidden",
  WebkitBackfaceVisibility: "hidden",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  willChange: "transform, opacity",
};

const MAX_STARS_DESKTOP = 18;
const MAX_STARS_MOBILE = 10;
const MAX_DASHES_DESKTOP = 12;
const MAX_DASHES_MOBILE = 8;
const MAX_RIPPLES = 3; // 2–4 looks great

/**
 * SlapCardAnimation (FULL REWRITE based on your uploaded 500+ line file)
 *
 * NEW FEATURES ADDED (without breaking your existing versions):
 * - targetCharacter "pops" into center rapidly BEFORE slap (when provided)
 * - on impact: target shakes quickly
 * - then target + infinity card vanish
 * - if targetIsLocal: requests CurrentPlayer to hide/re-show around slap
 *
 * Backward compatible:
 * - If you pass ONLY (card, version, onFinish), it behaves like your current file.
 *
 * New props you can pass from GameBoardScreen:
 * - targetCharacter: object for <CharacterCard {...targetCharacter} />
 * - targetIsLocal: boolean (true if the target character belongs to the client player)
 * - onRequestHideLocalCharacter: (hide:boolean) => void
 * - timestamp: number/string to force re-trigger animation even if card object is similar
 */
const SlapCardAnimation = ({
  card,
  version = 2,
  onFinish,
  disableSfx = false,

  // NEW (optional)
  targetCharacter = null,
  targetCard = null,
  targetIsLocal = false,
  onRequestHideLocalCharacter,
  timestamp,
}) => {
  const { deviceType } = useContext(MainContext);

  const cardControls = useAnimation();
  const flashControls = useAnimation();

  // NEW controls for the center target CharacterCard
  const targetControls = useAnimation();

  const [showSmoke, setShowSmoke] = useState(false);
  const [soundReady, setSoundReady] = useState(false);

  const [stars, setStars] = useState([]); // {id, dx, dy, size, dur, delay, spin}
  const [dashes, setDashes] = useState([]); // {id, dx, dy, len, th, rot, dur, delay}
  const [ripples, setRipples] = useState([]); // {id, scaleTo, dur, delay, bwStart, bwEnd, opacity}
  const [revealFrontOnly, setRevealFrontOnly] = useState(false);

  const soundRef = useRef(null);
  const isActiveRef = useRef(false);
  const timersRef = useRef(new Set());
  const playSfx = (name) => {
    if (!disableSfx && name) {
      AudioManager.playSFX?.(name);
    }
  };
  const schedule = (fn, ms) => {
    const id = setTimeout(() => {
      timersRef.current.delete(id);
      if (!isActiveRef.current) return;
      fn?.();
    }, ms);
    timersRef.current.add(id);
    return id;
  };
  const waitMs = (ms) =>
    new Promise((resolve) => {
      schedule(resolve, ms);
    });

  useEffect(() => {
    isActiveRef.current = true;
    const timers = timersRef.current;
    return () => {
      isActiveRef.current = false;
      timers.forEach((id) => clearTimeout(id));
      timers.clear();
    };
  }, []);

  // NOTE: In your original file, hue changes per render.
  // That’s fine; we keep it.
  const hue = Math.floor(Math.random() * 360);

  function spawnStars(deviceTypeArg = "desktop", opts = {}) {
    if (!isActiveRef.current) return;
    const baseCount =
      deviceTypeArg === "mobile" ? MAX_STARS_MOBILE : MAX_STARS_DESKTOP;
    const count = Math.max(
      0,
      Math.round(
        opts.count ??
          baseCount * (opts.countMult === undefined ? 1 : opts.countMult)
      )
    );
    const radiusMult = opts.radiusMult === undefined ? 1 : opts.radiusMult;
    const sizeMult = opts.sizeMult === undefined ? 1 : opts.sizeMult;
    const durationMult =
      opts.durationMult === undefined ? 1 : opts.durationMult;
    const delayMult = opts.delayMult === undefined ? 1 : opts.delayMult;
    const ttlMs = opts.ttlMs || 1000;

    const items = Array.from({ length: count }).map((_, i) => {
      const angle = Math.random() * Math.PI * 2;
      const radius = (120 + Math.random() * 160) * radiusMult;
      const size = (10 + Math.floor(Math.random() * 14)) * sizeMult;
      const dur = (0.35 + Math.random() * 0.3) * durationMult;
      const delay = (0.02 + Math.random() * 0.08) * delayMult;
      const spin = (Math.random() * 120 - 60) | 0;

      const dx = Math.cos(angle) * radius;
      const dy = Math.sin(angle) * radius;

      return {
        id: `star-${Date.now()}-${i}`,
        dx,
        dy,
        size,
        dur,
        delay,
        spin,
      };
    });

    setStars(items);
    schedule(() => setStars([]), ttlMs);
  }

  function spawnDashes(deviceTypeArg = "desktop", opts = {}) {
    if (!isActiveRef.current) return;
    const baseCount =
      deviceTypeArg === "mobile" ? MAX_DASHES_MOBILE : MAX_DASHES_DESKTOP;
    const count = Math.max(
      0,
      Math.round(
        opts.count ??
          baseCount * (opts.countMult === undefined ? 1 : opts.countMult)
      )
    );
    const travelMult = opts.travelMult === undefined ? 1 : opts.travelMult;
    const lenMult = opts.lengthMult === undefined ? 1 : opts.lengthMult;
    const thicknessMult =
      opts.thicknessMult === undefined ? 1 : opts.thicknessMult;
    const durationMult =
      opts.durationMult === undefined ? 1 : opts.durationMult;
    const delayMult = opts.delayMult === undefined ? 1 : opts.delayMult;
    const ttlMs = opts.ttlMs || 900;

    const items = Array.from({ length: count }).map((_, i) => {
      const angle = Math.random() * Math.PI * 2;
      const travel = (120 + Math.random() * 160) * travelMult;
      const len = (60 + Math.random() * 120) * lenMult;
      const th = (2 + Math.random() * 3) * thicknessMult;
      const dur = (0.28 + Math.random() * 0.25) * durationMult;
      const delay = (0.01 + Math.random() * 0.08) * delayMult;
      const rot = (angle * 180) / Math.PI;

      const dx = Math.cos(angle) * travel;
      const dy = Math.sin(angle) * travel;

      return {
        id: `dash-${Date.now()}-${i}`,
        dx,
        dy,
        len,
        th,
        rot,
        dur,
        delay,
      };
    });

    setDashes(items);
    schedule(() => setDashes([]), ttlMs);
  }

  function spawnRipples(opts = {}) {
    if (!isActiveRef.current) return;
    const count = Math.max(
      1,
      Math.round(
        opts.count ??
          Math.max(
            2,
            Math.min(4, MAX_RIPPLES * (opts.countMult === undefined ? 1 : opts.countMult))
          )
      )
    );
    const scaleMult = opts.scaleMult === undefined ? 1 : opts.scaleMult;
    const durationMult =
      opts.durationMult === undefined ? 1 : opts.durationMult;
    const delayMult = opts.delayMult === undefined ? 1 : opts.delayMult;
    const opacityMult = opts.opacityMult === undefined ? 1 : opts.opacityMult;
    const thicknessMult =
      opts.thicknessMult === undefined ? 1 : opts.thicknessMult;
    const ttlMs = opts.ttlMs || 1000;

    const items = Array.from({ length: count }).map((_, i) => {
      const scaleTo = (2.2 + Math.random() * 1.1) * scaleMult;
      const dur = (0.38 + Math.random() * 0.22) * durationMult;
      const delay = i * (0.05 + Math.random() * 0.03) * delayMult;
      const bwStart = (18 + Math.floor(Math.random() * 8)) * thicknessMult;
      const bwEnd = 2;
      const opacity = Math.min(0.95, 0.65 * opacityMult);

      return {
        id: `ripple-${Date.now()}-${i}`,
        scaleTo,
        dur,
        delay,
        bwStart,
        bwEnd,
        opacity,
      };
    });

    setRipples(items);
    schedule(() => setRipples([]), ttlMs);
  }

  const FX_PROFILES = {
    v1: {
      ripples: { count: 2, scaleMult: 0.95, durationMult: 0.9 },
      dashes: { countMult: 0.7, lengthMult: 0.95, durationMult: 0.9 },
      stars: { countMult: 0.65, sizeMult: 0.9, durationMult: 0.9 },
      dashesDelayMs: 20,
      starsDelayMs: 70,
    },
    v2: {
      ripples: { count: 3, scaleMult: 1, durationMult: 1 },
      dashes: { countMult: 1, lengthMult: 1, durationMult: 1 },
      stars: { countMult: 1, sizeMult: 1, durationMult: 1 },
      dashesDelayMs: 50,
      starsDelayMs: 100,
    },
    v3: {
      ripples: { count: 2, scaleMult: 1.1, opacityMult: 1.1 },
      dashes: { countMult: 1.45, lengthMult: 1.3, travelMult: 1.25 },
      stars: { countMult: 0.85, sizeMult: 0.9, radiusMult: 1.05 },
      dashesDelayMs: 8,
      starsDelayMs: 45,
    },
    v4: {
      ripples: { count: 4, scaleMult: 1.2, thicknessMult: 1.2, durationMult: 1.1 },
      dashes: { countMult: 0.75, lengthMult: 0.9, thicknessMult: 1.15 },
      stars: { countMult: 0.55, sizeMult: 0.85, durationMult: 0.85 },
      dashesDelayMs: 25,
      starsDelayMs: 95,
    },
    v5: {
      ripples: { count: 1, scaleMult: 0.9, durationMult: 0.8, opacityMult: 0.9 },
      dashes: { countMult: 1.15, lengthMult: 1.5, travelMult: 0.85, thicknessMult: 0.9 },
      stars: { countMult: 0.35, sizeMult: 0.8, radiusMult: 0.8, durationMult: 0.75 },
      dashesDelayMs: 0,
      starsDelayMs: 0,
    },
    v6: {
      ripples: { count: 2, scaleMult: 0.95, durationMult: 0.9 },
      dashes: { countMult: 1.4, lengthMult: 1.15, travelMult: 1.05, durationMult: 0.85 },
      stars: { countMult: 0.5, sizeMult: 0.85, durationMult: 0.8 },
      dashesDelayMs: 12,
      starsDelayMs: 70,
    },
    v7Fake: {
      ripples: { count: 1, scaleMult: 0.75, durationMult: 0.65, opacityMult: 0.5 },
      dashes: { countMult: 0.35, lengthMult: 0.7, durationMult: 0.65 },
      stars: { count: 0 },
      dashesDelayMs: 0,
      starsDelayMs: 0,
    },
    v7: {
      ripples: { count: 3, scaleMult: 1.05, durationMult: 0.95 },
      dashes: { countMult: 1.15, lengthMult: 1.1, travelMult: 1.05 },
      stars: { countMult: 0.9, sizeMult: 0.95, durationMult: 0.9 },
      dashesDelayMs: 15,
      starsDelayMs: 60,
    },
    v8: {
      ripples: { count: 2, scaleMult: 1, durationMult: 0.95, opacityMult: 0.85 },
      dashes: { countMult: 0.6, lengthMult: 0.95, travelMult: 0.95, durationMult: 0.85 },
      stars: { countMult: 1.35, sizeMult: 1.1, radiusMult: 1.2, durationMult: 1.05 },
      dashesDelayMs: 40,
      starsDelayMs: 5,
    },
    v9: {
      ripples: { count: 3, scaleMult: 1.05, durationMult: 0.9, opacityMult: 1 },
      dashes: { countMult: 0.95, lengthMult: 1.1, travelMult: 0.9, durationMult: 0.8 },
      stars: { countMult: 0.8, sizeMult: 0.95, radiusMult: 1, durationMult: 0.9 },
      dashesDelayMs: 5,
      starsDelayMs: 30,
    },
    v10: {
      ripples: { count: 2, scaleMult: 1, durationMult: 0.95, opacityMult: 0.9 },
      dashes: { countMult: 0.8, lengthMult: 1, travelMult: 0.85, durationMult: 0.85 },
      stars: { countMult: 0.6, sizeMult: 0.9, radiusMult: 0.95, durationMult: 0.9 },
      dashesDelayMs: 10,
      starsDelayMs: 40,
    },
  };

  const triggerImpactFX = (profileKey) => {
    if (!isActiveRef.current) return;
    const profile = FX_PROFILES[profileKey] || FX_PROFILES.v2;
    if (profile.ripples) spawnRipples(profile.ripples);
    if (profile.dashesDelayMs > 0) {
      schedule(() => spawnDashes(deviceType, profile.dashes), profile.dashesDelayMs);
    } else {
      spawnDashes(deviceType, profile.dashes);
    }
    if (profile.starsDelayMs > 0) {
      schedule(() => spawnStars(deviceType, profile.stars), profile.starsDelayMs);
    } else {
      spawnStars(deviceType, profile.stars);
    }
  };

  // Load sound
  useEffect(() => {
    if (disableSfx) return undefined;

    const sound = new Audio(
      require("../../@shared/assets/sounds/sfx-cards-woosh.mp3")
    );
    soundRef.current = sound;

    sound.oncanplaythrough = () => {
      setSoundReady(true);
      console.log("🎧 Sound loaded!");
    };

    return () => {
      soundRef.current && soundRef.current.pause();
      soundRef.current = null;
    };
  }, [disableSfx]);

  const getCardInitialForVersion = (v) => {
    if (v === 1) return { scale: 0.5, rotate: -60, y: -600, x: 0, opacity: 1 };
    if (v === 2) {
      return {
        x: 220,
        y: 280,
        scale: 0.7,
        rotateY: 8,
        rotateX: 45,
        transformPerspective: 300,
        opacity: 1,
      };
    }
    if (v === 3) {
      return {
        x: -320,
        y: -280,
        scale: 0.6,
        rotate: -28,
        rotateX: 22,
        rotateY: -14,
        opacity: 1,
      };
    }
    if (v === 4) return { x: 0, y: -420, scale: 0.85, rotate: 8, rotateX: -36, opacity: 1 };
    if (v === 5) return { x: -420, y: 85, scale: 0.92, rotate: -6, rotateX: 10, opacity: 1 };
    if (v === 6) {
      return {
        x: 360,
        y: 210,
        scale: 0.62,
        rotate: 34,
        rotateZ: 20,
        rotateX: 28,
        opacity: 1,
      };
    }
    if (v === 7) return { x: 0, y: -260, scale: 0.75, rotate: -4, rotateX: -20, opacity: 1 };
    if (v === 8) {
      return {
        x: 460,
        y: -300,
        scale: 0.5,
        rotate: 0,
        rotateZ: 0,
        rotateY: 65,
        rotateX: -12,
        opacity: 1,
      };
    }
    if (v === 9) {
      return {
        x: 280,
        y: 28,
        scale: 0.91,
        rotateY: 0,
        rotateX: 8,
        rotateZ: 8,
        transformPerspective: 1000,
        opacity: 1,
      };
    }
    if (v === 10) {
      return {
        x: 0,
        y: 0,
        scale: 0.88,
        rotateX: 0,
        transformPerspective: 1000,
        opacity: 1,
      };
    }
    return { scale: 0.5, rotate: -60, y: -600, x: 0, opacity: 1 };
  };

  // Run animation whenever card changes, OR timestamp changes (so same card can replay)
  useEffect(() => {
    if (!card) return;
    if (!isActiveRef.current) return;

    // reset one-off states
    setShowSmoke(false);
    setStars([]);
    setDashes([]);
    setRipples([]);
    setRevealFrontOnly(false);

    const raf = requestAnimationFrame(() => {
      (async () => {
        if (!isActiveRef.current) return;

        await flashControls.set({ opacity: 0 });
        await cardControls.set(getCardInitialForVersion(version));
        if (targetCharacter || targetCard) {
          await targetControls.set({ opacity: 0, scale: 0.7, x: 0, y: 0, rotateZ: 0 });
        }
        if (!isActiveRef.current) return;

        switch (version) {
          case 1:
            runAnimationV1();
            break;
          case 2:
            runAnimationV2();
            break;
          case 3:
            runAnimationV3();
            break;
          case 4:
            runAnimationV4();
            break;
          case 5:
            runAnimationV5();
            break;
          case 6:
            runAnimationV6();
            break;
          case 7:
            runAnimationV7();
            break;
          case 8:
            runAnimationV8();
            break;
          case 9:
            runAnimationV9();
            break;
          case 10:
            runAnimationV10();
            break;
          default:
            runAnimationV1();
            break;
        }
      })();
    });

    return () => cancelAnimationFrame(raf);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card, timestamp]);

  /**
   * NEW helper: show target character quickly in center before the slap
   */
  const popTargetIn = async () => {
    if (!targetCharacter && !targetCard) return;

    await targetControls.set({
      opacity: 0,
      scale: 0.7,
      y: 0,
      x: 0,
      rotateZ: 0,
    });

    await targetControls.start({
      opacity: 1,
      scale: 1,
      transition: { duration: 0.12, ease: "easeOut" },
    });
  };

  /**
   * NEW helper: shake target quickly on impact
   */
  const shakeTarget = async () => {
    if (!targetCharacter && !targetCard) return;
    await targetControls.start({
      x: [0, -10, 10, -8, 8, 0],
      transition: { duration: 0.18, ease: "easeInOut" },
    });
  };

  /**
   * NEW helper: vanish target at end
   */
  const vanishTarget = async () => {
    if (!targetCharacter && !targetCard) return;
    await targetControls.start({
      opacity: 0,
      scale: 0.96,
      transition: { duration: 0.12, ease: "easeIn" },
    });
  };

  /**
   * Version 1 (your original)
   * Updated to optionally pop/shake/vanish the target CharacterCard too.
   */
  const runAnimationV1 = () => {
    // If local target, hide their CurrentPlayer character immediately
    if (targetIsLocal) onRequestHideLocalCharacter?.(true);

    // pop target in right away (non-blocking)
    popTargetIn();

    // Start the entry animation concurrently:
    cardControls.start({
      scale: [0.5, 1.5, 1.2, 1.3],
      rotate: [-60, 0],
      y: [-600, 0, -15, 0],
      transition: {
        scale: { duration: 0.71, ease: "easeInOut" },
        rotate: { duration: 0.4, ease: "easeInOut" },
        y: {
          duration: 0.72,
          ease: "easeInOut",
          times: [0, 0.5 / 0.72, (0.5 + 0.12) / 0.72, 1],
        },
      },
    });

    // After 600ms: trigger flash overlay and shake effect + target shake
    schedule(() => {
      if (!isActiveRef.current) return;
      flashControls.start({
        opacity: [0, 0.8, 0],
        transition: { duration: 0.23, ease: "linear" },
      });

      cardControls.start({
        x: [0, 10, -10, 6, -6, 0],
        transition: { duration: 0.25, ease: "easeInOut" },
      });

      // NEW: target shakes on impact
      shakeTarget();
      triggerImpactFX("v1");

      // Play the slap sound if it's loaded
      if (!disableSfx && soundReady && soundRef.current) {
        soundRef.current.currentTime = 0;
        soundRef.current.play();
        console.log("🔊 Playing slap sound...");
      } else if (!disableSfx) {
        console.warn("⚠️ Sound not loaded yet!");
      }
    }, 600);

    // After 700ms: show smoke
    schedule(() => {
      setShowSmoke(true);
      playSfx("cardSlapPackOpen");
    }, 700);

    // After 700ms: small post-impact spin (your original comment said 1100 but used 700)
    schedule(() => {
      if (!isActiveRef.current) return;
      cardControls.start({
        rotate: 10,
        transition: { duration: 0.3, ease: "easeInOut" },
      });
    }, 700);

    // After 1500ms: fade out card + target, then onFinish + re-show local
    schedule(() => {
      if (!isActiveRef.current) return;
      cardControls.start({
        opacity: 0,
        transition: { duration: 0.35, ease: "easeInOut" },
      });

      // NEW: vanish target too
      vanishTarget();

      schedule(() => {
        if (!isActiveRef.current) return;
        if (targetIsLocal) onRequestHideLocalCharacter?.(false);
        if (onFinish) onFinish();
      }, 350);
    }, 1500);
  };

  /**
   * Version 2 (your original async cinematic slap)
   * Updated to:
   * - show target CharacterCard BEFORE slap
   * - shake target on impact
   * - vanish both at end
   * - hide/re-show local CurrentPlayer character if needed
   */
  const runAnimationV2 = async () => {
    // If local target, hide their CurrentPlayer character immediately
    if (targetIsLocal) onRequestHideLocalCharacter?.(true);

    // Reset target state each run
    if (targetCharacter) {
      await targetControls.set({ opacity: 0, scale: 0.7, x: 0, y: 0 });
      await popTargetIn();
      // tiny beat so the target is clearly seen before the slap approaches
      await waitMs(80);
    }

    // --- PHASE 1: Lift & approach from bottom-right toward center ---
    await cardControls.start({
      x: [220, 120, 40, 0],
      y: [280, 160, 60, 10],
      scale: [0.7, 1.85, 2.15, 1.12],
      rotateY: [8, 6, 2, 0],
      rotateX: [45, 40, -28, 40],
      transformPerspective: [600, 300, 620, 300],
      transition: {
        duration: 0.6,
        ease: [0.22, 0.61, 0.36, 1],
        times: [0, 0.25, 0.75, 1],
      },
    });

    playSfx("cardWhooshIn");

    // --- PHASE 2: Wind-up (tiny hover before slap) ---
    await cardControls.start({
      y: [10, -6, -4, 0],
      rotateX: [22, 18, 16, 14],
      scale: [1.12, 1.14, 1.13, 1.12],
      transition: {
        duration: 0.2,
        ease: "easeInOut",
        times: [0, 0.5, 0.8, 1],
      },
    });

    // --- PHASE 3: IMPACT (“slap down”) ---
    const impact = cardControls.start({
      y: [0, 10, 0],
      rotateX: [14, 4, 6],
      scale: [1.12, 1.08, 1.1],
      boxShadow: [
        "6px 8px 18px rgba(0,0,0,0.35)",
        "2px 4px 10px rgba(0,0,0,0.28)",
        "5px 6px 16px rgba(0,0,0,0.32)",
      ],
      transition: {
        duration: 0.18,
        ease: "easeIn",
        times: [0, 0.5, 1],
      },
    });

    const flash = flashControls.start({
      opacity: [0, 0.75, 0],
      transition: { duration: 0.18, ease: "linear", times: [0, 0.5, 1] },
    });

    const shakeCard = cardControls.start({
      x: [0, 8, -8, 5, -4, 0],
      transition: { duration: 0.22, ease: "easeInOut" },
    });

    // NEW: shake target at impact
    const shakeChar = shakeTarget();

    playSfx("cardSlap");

    // 🌟 spawn ripples/dashes/stars on impact frame
    triggerImpactFX("v2");

    await Promise.all([impact, flash, shakeCard, shakeChar]);

    // --- PHASE 4: Post-impact settle ---
    await cardControls.start({
      y: [0, -2, 0],
      rotateX: [6, 8, 7],
      rotateZ: [0, 6, 12],
      scale: [1.1, 1.085, 1.09],
      transition: {
        duration: 0.22,
        ease: "linear",
        times: [0, 0.6, 1],
      },
    });

    // Optional smoke puff after slap
    schedule(() => {
      setShowSmoke(true);
      playSfx("cardSlapPackOpen");
    }, 70);

    // End: fade out card + target, then finish
    await Promise.all([
      cardControls.start({
        opacity: [1, 1, 0.0],
        transition: { duration: 0.25, ease: "easeOut", delay: 0.35 },
      }),
      vanishTarget(),
    ]);

    // Re-show local character after slap finishes
    if (targetIsLocal) onRequestHideLocalCharacter?.(false);

    onFinish?.();
  };

  /**
   * Version 3
   * Fast diagonal whip-in with heavier impact shake.
   */
  const runAnimationV3 = async () => {
    if (targetIsLocal) onRequestHideLocalCharacter?.(true);

    if (targetCharacter) {
      await targetControls.set({ opacity: 0, scale: 0.78, x: 0, y: 0 });
      await popTargetIn();
    }

    await cardControls.set({
      x: -320,
      y: -280,
      scale: 0.6,
      rotate: -28,
      rotateX: 22,
      rotateY: -14,
      opacity: 1,
    });

    await cardControls.start({
      x: [-320, -120, -30, 0],
      y: [-280, -120, -35, 0],
      scale: [0.6, 1.4, 1.2, 1.14],
      rotate: [-28, -8, 2, 0],
      rotateX: [22, 10, 4, 6],
      rotateY: [-14, -6, -2, 0],
      transition: {
        duration: 0.45,
        ease: [0.22, 0.61, 0.36, 1],
        times: [0, 0.45, 0.82, 1],
      },
    });

    const impact = cardControls.start({
      y: [0, 14, 0],
      scale: [1.14, 1.05, 1.1],
      rotateX: [6, -2, 5],
      transition: { duration: 0.2, ease: "easeInOut", times: [0, 0.5, 1] },
    });
    const shakeCard = cardControls.start({
      x: [0, -12, 12, -9, 8, -5, 0],
      transition: { duration: 0.28, ease: "easeInOut" },
    });
    const flash = flashControls.start({
      opacity: [0, 0.85, 0],
      transition: { duration: 0.18, ease: "linear" },
    });
    const shakeChar = shakeTarget();

    playSfx("cardSlap");
    triggerImpactFX("v3");

    await Promise.all([impact, shakeCard, flash, shakeChar]);

    await cardControls.start({
      rotateZ: [0, -6, 4, 0],
      scale: [1.1, 1.07, 1.09],
      transition: { duration: 0.22, ease: "easeOut" },
    });

    await Promise.all([
      cardControls.start({
        opacity: [1, 1, 0],
        y: [0, 10, 18],
        transition: { duration: 0.32, ease: "easeOut", delay: 0.15 },
      }),
      vanishTarget(),
    ]);

    if (targetIsLocal) onRequestHideLocalCharacter?.(false);
    onFinish?.();
  };

  /**
   * Version 4
   * Vertical drop slam from above with dramatic squash and rebound.
   */
  const runAnimationV4 = async () => {
    if (targetIsLocal) onRequestHideLocalCharacter?.(true);

    if (targetCharacter) {
      await targetControls.set({ opacity: 0, scale: 0.72, x: 0, y: 0 });
      await popTargetIn();
      await waitMs(90);
    }

    await cardControls.set({
      x: 0,
      y: -420,
      scale: 0.85,
      rotate: 8,
      rotateX: -36,
      opacity: 1,
    });

    await cardControls.start({
      y: [-420, -220, -100, -25, 0],
      scale: [0.85, 0.96, 1.04, 1.1, 1.13],
      rotate: [8, 6, 3, 1, 0],
      rotateX: [-36, -18, -8, -2, 8],
      transition: {
        duration: 0.48,
        ease: [0.22, 0.61, 0.36, 1],
        times: [0, 0.35, 0.65, 0.86, 1],
      },
    });

    const slam = cardControls.start({
      y: [0, 16, -4, 0],
      scaleY: [1, 0.88, 1.06, 1],
      scaleX: [1, 1.15, 0.98, 1],
      transition: {
        duration: 0.28,
        ease: "easeInOut",
        times: [0, 0.35, 0.72, 1],
      },
    });
    const flash = flashControls.start({
      opacity: [0, 0.92, 0],
      transition: { duration: 0.16, ease: "linear" },
    });
    const shakeCard = cardControls.start({
      x: [0, 6, -6, 4, -4, 0],
      transition: { duration: 0.18, ease: "easeInOut" },
    });
    const shakeChar = shakeTarget();

    playSfx("cardSlap");
    triggerImpactFX("v4");

    await Promise.all([slam, flash, shakeCard, shakeChar]);

    await cardControls.start({
      rotateZ: [0, 8, 4],
      transition: { duration: 0.2, ease: "easeOut" },
    });

    await Promise.all([
      cardControls.start({
        opacity: [1, 1, 0],
        transition: { duration: 0.3, ease: "easeOut", delay: 0.18 },
      }),
      vanishTarget(),
    ]);

    if (targetIsLocal) onRequestHideLocalCharacter?.(false);
    onFinish?.();
  };

  /**
   * Version 5
   * Poker table side-slide: card skims in low, then hard table tap.
   */
  const runAnimationV5 = async () => {
    if (targetIsLocal) onRequestHideLocalCharacter?.(true);

    if (targetCharacter) {
      await targetControls.set({ opacity: 0, scale: 0.82, x: 0, y: 0 });
      await popTargetIn();
    }

    await cardControls.set({
      x: -420,
      y: 85,
      scale: 0.92,
      rotate: -6,
      rotateX: 10,
      opacity: 1,
    });

    await cardControls.start({
      x: 0,
      y: 0,
      scale: 1.1,
      rotate: 0,
      rotateX: 4,
      transition: {
        duration: 0.44,
        ease: [0.16, 1, 0.3, 1],
      },
    });

    const tap = cardControls.start({
      y: [0, 11, 0],
      scaleY: [1, 0.94, 1],
      scaleX: [1, 1.08, 1],
      transition: { duration: 0.22, ease: "easeInOut" },
    });
    const shakeCard = cardControls.start({
      x: [0, 9, -9, 6, -4, 0],
      transition: { duration: 0.2, ease: "easeInOut" },
    });
    const flash = flashControls.start({
      opacity: [0, 0.72, 0],
      transition: { duration: 0.16, ease: "linear" },
    });
    const shakeChar = shakeTarget();

    playSfx("cardSlap");
    triggerImpactFX("v5");

    await Promise.all([tap, shakeCard, flash, shakeChar]);

    await Promise.all([
      cardControls.start({
        opacity: [1, 1, 0],
        y: [0, 6, 14],
        rotateZ: [0, -7, -10],
        transition: { duration: 0.28, delay: 0.14, ease: "easeOut" },
      }),
      vanishTarget(),
    ]);

    if (targetIsLocal) onRequestHideLocalCharacter?.(false);
    onFinish?.();
  };

  /**
   * Version 6
   * Dealer flick: quick wrist-spin into center, then snap impact.
   */
  const runAnimationV6 = async () => {
    if (targetIsLocal) onRequestHideLocalCharacter?.(true);

    if (targetCharacter) {
      await targetControls.set({ opacity: 0, scale: 0.78, x: 0, y: 0 });
      await popTargetIn();
      await waitMs(70);
    }

    await cardControls.set({
      x: 360,
      y: 210,
      scale: 0.62,
      rotate: 34,
      rotateZ: 20,
      rotateX: 28,
      opacity: 1,
    });

    await cardControls.start({
      x: [360, 180, 60, 0],
      y: [210, 120, 30, 0],
      scale: [0.62, 1.25, 1.12, 1.08],
      rotate: [34, 14, 4, 0],
      rotateZ: [20, -8, 6, 0],
      rotateX: [28, 14, 8, 6],
      transition: {
        duration: 0.46,
        ease: [0.22, 0.61, 0.36, 1],
        times: [0, 0.42, 0.82, 1],
      },
    });

    const impact = cardControls.start({
      y: [0, 10, 0],
      scale: [1.08, 1.03, 1.07],
      transition: { duration: 0.18, ease: "easeInOut" },
    });
    const shakeCard = cardControls.start({
      x: [0, -7, 7, -5, 4, 0],
      transition: { duration: 0.2, ease: "easeInOut" },
    });
    const flash = flashControls.start({
      opacity: [0, 0.78, 0],
      transition: { duration: 0.16, ease: "linear" },
    });
    const shakeChar = shakeTarget();

    playSfx("cardSlap");
    triggerImpactFX("v6");

    await Promise.all([impact, shakeCard, flash, shakeChar]);

    await cardControls.start({
      rotateZ: [0, 10, -4, 0],
      transition: { duration: 0.22, ease: "easeOut" },
    });

    await Promise.all([
      cardControls.start({
        opacity: [1, 1, 0],
        transition: { duration: 0.27, delay: 0.16, ease: "easeOut" },
      }),
      vanishTarget(),
    ]);

    if (targetIsLocal) onRequestHideLocalCharacter?.(false);
    onFinish?.();
  };

  /**
   * Version 7
   * Double-knock bluff: light fake hit then stronger second slam.
   */
  const runAnimationV7 = async () => {
    if (targetIsLocal) onRequestHideLocalCharacter?.(true);

    if (targetCharacter) {
      await targetControls.set({ opacity: 0, scale: 0.8, x: 0, y: 0 });
      await popTargetIn();
    }

    await cardControls.set({
      x: 0,
      y: -260,
      scale: 0.75,
      rotate: -4,
      rotateX: -20,
      opacity: 1,
    });

    await cardControls.start({
      y: [-260, -120, -30, 0],
      scale: [0.75, 0.94, 1.04, 1.08],
      rotateX: [-20, -8, 2, 6],
      rotate: [-4, -2, -1, 0],
      transition: {
        duration: 0.4,
        ease: [0.22, 0.61, 0.36, 1],
        times: [0, 0.5, 0.84, 1],
      },
    });

    // Fake first knock
    await Promise.all([
      cardControls.start({
        y: [0, 6, 0],
        scale: [1.08, 1.05, 1.07],
        transition: { duration: 0.14, ease: "easeInOut" },
      }),
      flashControls.start({
        opacity: [0, 0.25, 0],
        transition: { duration: 0.1, ease: "linear" },
      }),
    ]);
    triggerImpactFX("v7Fake");

    await waitMs(70);

    const secondHit = cardControls.start({
      y: [0, 14, -1, 0],
      scaleY: [1, 0.9, 1.04, 1],
      scaleX: [1, 1.12, 0.99, 1],
      transition: {
        duration: 0.24,
        ease: "easeInOut",
        times: [0, 0.45, 0.74, 1],
      },
    });
    const shakeCard = cardControls.start({
      x: [0, 10, -10, 7, -5, 0],
      transition: { duration: 0.24, ease: "easeInOut" },
    });
    const flash = flashControls.start({
      opacity: [0, 0.86, 0],
      transition: { duration: 0.15, ease: "linear" },
    });
    const shakeChar = shakeTarget();

    playSfx("cardSlap");
    triggerImpactFX("v7");

    await Promise.all([secondHit, shakeCard, flash, shakeChar]);

    await Promise.all([
      cardControls.start({
        opacity: [1, 1, 0],
        y: [0, 8, 16],
        transition: { duration: 0.3, delay: 0.15, ease: "easeOut" },
      }),
      vanishTarget(),
    ]);

    if (targetIsLocal) onRequestHideLocalCharacter?.(false);
    onFinish?.();
  };

  /**
   * Version 8
   * Spin toss: card flies in with a fast spin, then lands at center.
   */
  const runAnimationV8 = async () => {
    if (targetIsLocal) onRequestHideLocalCharacter?.(true);

    if (targetCharacter) {
      await targetControls.set({ opacity: 0, scale: 0.78, x: 0, y: 0 });
      await popTargetIn();
    }

    await cardControls.set({
      x: 460,
      y: -300,
      scale: 0.5,
      rotate: 0,
      rotateZ: 0,
      rotateY: 65,
      rotateX: -12,
      opacity: 1,
    });

    await cardControls.start({
      x: 0,
      y: 0,
      scale: 1.1,
      rotateZ: 720,
      rotateY: 0,
      rotateX: 6,
      transition: {
        duration: 0.68,
        ease: [0.18, 0.84, 0.24, 1],
      },
    });

    // Soft settle before impact to avoid a harsh stop.
    await cardControls.start({
      y: [0, 2, 0],
      scale: [1.1, 1.085, 1.09],
      transition: { duration: 0.12, ease: "easeOut" },
    });

    const impact = cardControls.start({
      y: [0, 10, 0],
      scale: [1.09, 1.05, 1.08],
      transition: { duration: 0.22, ease: "easeInOut" },
    });
    const shakeCard = cardControls.start({
      x: [0, -7, 7, -5, 4, 0],
      transition: { duration: 0.24, ease: "easeInOut" },
    });
    const flash = flashControls.start({
      opacity: [0, 0.8, 0],
      transition: { duration: 0.16, ease: "linear" },
    });
    const shakeChar = shakeTarget();

    playSfx("cardSlap");
    triggerImpactFX("v8");

    await Promise.all([impact, shakeCard, flash, shakeChar]);

    await Promise.all([
      cardControls.start({
        opacity: [1, 1, 0],
        y: [0, 8, 16],
        rotateZ: [0, 8, 12],
        transition: { duration: 0.3, delay: 0.14, ease: "easeOut" },
      }),
      vanishTarget(),
    ]);

    if (targetIsLocal) onRequestHideLocalCharacter?.(false);
    onFinish?.();
  };

  /**
   * Version 9
   * Reveal: face-down slide in, then a hard flip reveal into the slap.
   */
  const runAnimationV9 = async () => {
    if (targetIsLocal) onRequestHideLocalCharacter?.(true);

    if (targetCharacter || targetCard) {
      await targetControls.set({ opacity: 0, scale: 0.8, x: 0, y: 0 });
      await popTargetIn();
      await waitMs(70);
    }

    await cardControls.set({
      x: 280,
      y: 28,
      scale: 0.91,
      rotateY: 0,
      rotateX: 8,
      rotateZ: 8,
      transformPerspective: 1000,
      opacity: 1,
    });

    const revealDuration = 0.42;
    const impactCueMs = 300;

    const revealFlip = cardControls.start({
      x: [280, 186, 86, 18, 0],
      y: [28, 22, 12, -4, 0],
      scale: [0.91, 0.96, 1.01, 1.07, 1.04],
      rotateY: [0, 0, 18, 126, REVEAL_FRONT_ROTATE_Y],
      rotateX: [8, 7, 4, -10, 8],
      rotateZ: [8, 6, 2, -2, 0],
      transition: {
        duration: revealDuration,
        ease: [0.2, 0.8, 0.24, 1],
        times: [0, 0.24, 0.5, 0.8, 1],
      },
    });

    schedule(() => {
      if (!isActiveRef.current) return;

      flashControls.start({
        opacity: [0, 0.88, 0],
        transition: { duration: 0.14, ease: "easeOut" },
      });

      shakeTarget();
      playSfx("cardSlap");
      triggerImpactFX("v9");
    }, impactCueMs);

    await revealFlip;
    setRevealFrontOnly(true);
    await waitMs(500);

    await Promise.all([
      cardControls.start({
        opacity: [1, 1, 0],
        y: [0, 4, 14],
        rotateY: REVEAL_FRONT_ROTATE_Y,
        rotateZ: [0, 3, 7],
        transition: { duration: 0.24, delay: 0.02, ease: "easeInOut" },
      }),
      vanishTarget(),
    ]);

    if (targetIsLocal) onRequestHideLocalCharacter?.(false);
    onFinish?.();
  };

  /**
   * Version 10
   * Fall Forward: rises from below showing the upside-down back,
   * then flips on the Y axis to reveal the front upright.
   */
  const runAnimationV10 = async () => {
    if (targetIsLocal) onRequestHideLocalCharacter?.(true);

    if (targetCharacter || targetCard) {
      await targetControls.set({ opacity: 0, scale: 0.8, x: 0, y: 0 });
      await popTargetIn();
      await waitMs(60);
    }

    await cardControls.set({
      x: 0,
      y: 0,
      scale: 0.88,
      rotateX: 0,
      transformPerspective: 1000,
      opacity: 1,
    });

    const fallDuration = 0.48;
    const impactCueMs = 320;

    const fallForward = cardControls.start({
      y: [0, 308, 184, 94, 380],
      scale: [0.88, 0.93, 0.98, 1.03, 1.03],
      rotateX: [0, 0, 16, 124, FALL_FORWARD_FRONT_ROTATE_X],
      transition: {
        duration: fallDuration,
        ease: [0.2, 0.8, 0.24, 1],
        times: [0, 0.34, 0.64, 0.88, 1],
      },
    });

    schedule(() => {
      if (!isActiveRef.current) return;

      flashControls.start({
        opacity: [0, 0.8, 0],
        transition: { duration: 0.14, ease: "easeOut" },
      });

      shakeTarget();
      playSfx("cardSlap");
      triggerImpactFX("v10");
    }, impactCueMs);

    await fallForward;
    setRevealFrontOnly(true);
    await waitMs(360);

    await Promise.all([
      cardControls.start({
        opacity: [1, 1, 0],
        y: [64, 68, 78],
        rotateX: FALL_FORWARD_FRONT_ROTATE_X,
        transition: { duration: 0.24, delay: 0.02, ease: "easeInOut" },
      }),
      vanishTarget(),
    ]);

    if (targetIsLocal) onRequestHideLocalCharacter?.(false);
    onFinish?.();
  };

  if (!card) return null;

  const animatedCardHeight =
    window.innerWidth > 900 ? window.innerHeight * 0.38 : window.innerHeight * 0.3;
  const animatedCardWidth = animatedCardHeight * CARD_ASPECT_RATIO;
  const usesRevealFaces = version === 9 || version === 10;
  const animatedCardBoxShadow = usesRevealFaces
    ? "none"
    : "6px 8px 18px rgba(0,0,0,0.35)";
  const revealBackImageTransform = version === 10 ? "rotate(180deg)" : "none";
  const revealBackFaceTransform =
    version === 10 ? "rotateX(0deg) translateZ(0px)" : "rotateY(0deg) translateZ(0px)";
  const revealFrontFaceTransform =
    version === 10
      ? `rotateX(${FALL_FORWARD_FRONT_ROTATE_X}deg) translateZ(1px)`
      : `rotateY(${REVEAL_FRONT_ROTATE_Y}deg) translateZ(1px)`;
  const cardTransformOrigin = version === 10 ? "center top" : "center center";

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 6000,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        pointerEvents: "none",
      }}
    >
      {/* Flash Overlay */}
      <motion.div
        animate={flashControls}
        initial={{ opacity: 0 }}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "#fff",
          zIndex: 10000,
        }}
      />

      {/* Smoke Puff (still commented out like your original file) */}
      {/* {showSmoke && (
        <SpriteAnimator
          animationName="smokepuff1"
          frameWidth={64}
          frameHeight={64}
          frameCount={12}
          fps={20}
          loop={false}
          style={{
            position: "absolute",
            bottom: "-35%",
            width: "400px",
            height: "400px",
            alignSelf: "center",
            transform: "scale(4)",
          }}
        />
      )} */}

      {/* Background Ripples (behind target + card) */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          pointerEvents: "none",
          zIndex: 5850,
        }}
      >
        {ripples.map((r) => (
          <motion.div
            key={r.id}
            initial={{
              scale: 0.2,
              opacity: 0,
              borderWidth: r.bwStart,
            }}
            animate={{
              scale: r.scaleTo,
              opacity: [0, r.opacity, 0],
              borderWidth: r.bwEnd,
            }}
            transition={{
              duration: r.dur,
              delay: r.delay,
              ease: [0.22, 0.61, 0.36, 1],
            }}
            style={{
              position: "absolute",
              width: 220,
              height: 220,
              borderRadius: "50%",
              borderStyle: "solid",
              borderColor: "rgba(255,255,255,0.8)",
              boxShadow:
                "0 0 20px rgba(255,255,255,0.45), inset 0 0 12px rgba(255,255,255,0.35)",
            }}
          />
        ))}
      </div>

      {/* Light Dash Lines */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          pointerEvents: "none",
          zIndex: 9050,
        }}
      >
        {dashes.map((d) => (
          <motion.div
            key={d.id}
            initial={{
              x: 0,
              y: 0,
              opacity: 0,
              width: 0,
              height: d.th,
              rotate: d.rot,
              scaleY: 1,
              filter: "drop-shadow(0 0 6px rgba(255,255,255,0.6))",
            }}
            animate={{
              x: d.dx,
              y: d.dy,
              width: [0, d.len, d.len * 0.8],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: d.dur,
              delay: d.delay,
              ease: [0.22, 0.61, 0.36, 1],
            }}
            style={{
              position: "absolute",
              background:
                "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.95) 45%, rgba(255,255,255,0) 100%)",
              borderRadius: d.th,
              filter: `hue-rotate(${hue}deg) brightness(1.1)`,
            }}
          />
        ))}
      </div>

      {/* Stars Burst */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          pointerEvents: "none",
          zIndex: 9000,
        }}
      >
        {stars.map((s) => (
          <motion.img
            key={s.id}
            src={starShine}
            alt=""
            initial={{
              x: 0,
              y: 0,
              opacity: 0,
              scale: 0.6,
              rotate: 0,
            }}
            animate={{
              x: s.dx,
              y: s.dy,
              opacity: [0, 1, 0],
              scale: [0.6, 1, 0.8],
              rotate: s.spin,
            }}
            transition={{
              duration: s.dur,
              ease: [0.22, 0.61, 0.36, 1],
              delay: s.delay,
            }}
            style={{
              width: s.size,
              height: s.size,
              position: "absolute",
              filter: "drop-shadow(0 0 6px rgba(255,255,255,0.9))",
            }}
          />
        ))}
      </div>

      {/* NEW: Target Character Card in Center (pops in before slap) */}
      {(targetCharacter || targetCard) && (
        <motion.div
          animate={targetControls}
          initial={{ opacity: 0, scale: 0.7 }}
          style={{
            position: "absolute",
            left: window.innerWidth > 900 ? '36%' : "12%",
            rotate:"-8deg",
            zIndex: 5950, // behind infinity card (6000) but above ripples
            height:
              window.innerWidth > 900
                ? window.innerHeight * 0.45
                : window.innerHeight * 0.34,
            borderRadius: "12px",
          }}
        >
          {targetCard ? (
            <InfinityCard
              {...targetCard}
              heightPer={100}
              borderPx={8}
              containerHeight={
                window.innerWidth > 900
                  ? window.innerHeight * 0.42
                  : window.innerHeight * 0.34
              }
            />
          ) : (
            <CharacterCard
              {...targetCharacter}
              heightPer={85}
              containerHeight={
                window.innerWidth > 900
                  ? window.innerHeight * 0.42
                  : window.innerHeight * 0.34
              }
            />
          )}
        </motion.div>
      )}

      {/* Animated Infinity Card */}
      <motion.div
        animate={cardControls}
        initial={getCardInitialForVersion(version)}
        style={{
          zIndex: 6000,
          boxShadow: animatedCardBoxShadow,
          height: animatedCardHeight,
          width: animatedCardWidth,
          borderRadius: "12px",
          position: "relative",
          transformStyle: "preserve-3d",
          transformOrigin: cardTransformOrigin,
        }}
      >
        {usesRevealFaces ? (
          <>
            {!revealFrontOnly && (
              <div
                style={{
                  ...cardFaceStyle,
                  transform: revealBackFaceTransform,
                  backgroundColor: "#000",
                }}
              >
                <img
                  src={backImage}
                  alt="card back"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transform: revealBackImageTransform,
                  }}
                />
              </div>
            )}

            <div
              style={{
                ...cardFaceStyle,
                transform: revealFrontFaceTransform,
              }}
            >
              <InfinityCard
                {...card}
                heightPer={90}
                borderPx={8}
                containerHeight={animatedCardHeight}
              />
            </div>
          </>
        ) : (
          <InfinityCard
            {...card}
            heightPer={100}
            borderPx={8}
            containerHeight={animatedCardHeight}
          />
        )}
      </motion.div>
    </div>
  );
};

export default SlapCardAnimation;



